import { useMemo, useState, type ReactNode } from 'react';
import { Check, Lock, Search, X } from 'lucide-react';
import { naira } from '@/lib/format';
import { postAuthPinChange, postAuthPinSet } from '@/api/auth';
import type { WalletBank, WalletQuote } from '@/api/parent';

/** Bottom sheet on a phone, centred card on a computer. */
export function Sheet({ title, sub, onClose, children, footer, z = 'z-50' }: { title: string; sub?: string; onClose: () => void; children: ReactNode; footer?: ReactNode; z?: string }) {
  return (
    <div className={`fixed inset-0 ${z} bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center`} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="min-w-0"><h3 className="font-bold text-slate-900 text-lg">{title}</h3>{sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}</div>
          <button onClick={onClose} className="p-1 -mr-1 text-slate-400 hover:text-slate-700" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex-1">{children}</div>
        {footer && <div className="px-5 py-3 border-t border-slate-100 flex gap-2">{footer}</div>}
      </div>
    </div>
  );
}

export const btn = 'flex-1 py-3 rounded-xl text-sm font-bold disabled:opacity-40';
export const primary = `${btn} bg-[#0A2540] text-white`;
export const ghost = `${btn} border border-slate-300 text-slate-700`;
export const input = 'w-full border border-slate-300 rounded-xl px-3 py-3 text-sm focus:border-[#0A2540] focus:ring-2 focus:ring-[#0A2540]/10 outline-none bg-white';

/** The same PIN prompt the school owner sees: one big masked box, Enter to confirm. */
export function PinPrompt({ title, sub = 'Enter your 4-digit transaction PIN', error, busy, onCancel, onConfirm }: { title: string; sub?: string; error?: string; busy?: boolean; onCancel: () => void; onConfirm: (pin: string) => void }) {
  const [pin, setPin] = useState('');
  const go = () => pin.length === 4 && !busy && onConfirm(pin);
  return (
    <Sheet title={title} sub={sub} onClose={onCancel} z="z-[70]"
      footer={<><button onClick={onCancel} className={ghost}>Cancel</button><button onClick={go} disabled={pin.length !== 4 || busy} className={primary}>{busy ? 'Processing…' : 'Confirm'}</button></>}>
      <div className="flex justify-center mb-3"><span className="w-12 h-12 rounded-full bg-slate-100 grid place-items-center"><Lock className="w-5 h-5 text-slate-600" /></span></div>
      <input type="password" inputMode="numeric" autoComplete="one-time-code" maxLength={4} autoFocus value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} onKeyDown={(e) => e.key === 'Enter' && go()}
        className="w-full text-center text-3xl tracking-[0.6em] font-mono border-2 border-slate-300 rounded-2xl py-4 focus:border-[#0A2540] outline-none" aria-label="PIN" />
      {error && <p className="text-sm text-red-600 mt-3 text-center">{error}</p>}
    </Sheet>
  );
}

/** Amount + each charge (OJP charge, stamp duty, SMS alert…) = Total. */
export function QuoteBox({ quote, amount, amountLabel = 'Amount', loading }: { quote: WalletQuote | null; amount: number; amountLabel?: string; loading?: boolean }) {
  const lines = quote?.lines?.length ? quote.lines : quote && quote.charge > 0 ? [{ name: 'OJP charge', amount: quote.charge }] : [];
  return (
    <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm space-y-1.5">
      <div className="flex justify-between"><span className="text-slate-600">{amountLabel}</span><span className="font-mono">{naira(quote?.amount ?? amount)}</span></div>
      {loading ? <div className="flex justify-between text-slate-400"><span>Charges</span><span>…</span></div>
        : lines.length ? lines.map((l, i) => <div key={i} className="flex justify-between"><span className="text-slate-600">{l.name}</span><span className="font-mono">{naira(l.amount)}</span></div>)
          : quote && <div className="flex justify-between"><span className="text-slate-600">Charges</span><span className="font-mono">{naira(0)}</span></div>}
      <div className="flex justify-between font-bold text-base border-t border-slate-200 pt-2"><span>Total</span><span className="font-mono">{quote ? naira(quote.total) : '…'}</span></div>
    </div>
  );
}

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/** Bank chooser: type a few letters or tap a starting letter, then tap the bank. */
export function BankPicker({ banks, loading, value, onChange }: { banks: WalletBank[]; loading?: boolean; value: string; onChange: (code: string) => void }) {
  const [q, setQ] = useState('');
  const [letter, setLetter] = useState('');
  const chosen = banks.find((b) => b.code === value);
  const list = useMemo(() => {
    const t = q.trim().toUpperCase();
    return banks.filter((b) => { const n = b.name.toUpperCase(); return (!letter || n.startsWith(letter)) && (!t || n.includes(t) || b.code.includes(t)); });
  }, [banks, q, letter]);
  const present = useMemo(() => new Set(banks.map((b) => b.name.charAt(0).toUpperCase())), [banks]);

  if (chosen) {
    return (
      <div className="flex items-center justify-between gap-3 border border-green-300 bg-green-50 rounded-xl px-3 py-3">
        <span className="text-sm font-semibold text-slate-900 truncate"><Check className="w-4 h-4 inline -mt-0.5 mr-1 text-green-700" />{chosen.name}</span>
        <button type="button" onClick={() => onChange('')} className="text-xs font-semibold text-[#0A2540] shrink-0">Change</button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={loading ? 'Loading banks…' : 'Type the bank name, e.g. "acc", "opay"'} className={`${input} pl-9`} /></div>
      <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1">
        <button type="button" onClick={() => setLetter('')} className={`px-2.5 h-7 rounded-lg text-xs font-bold shrink-0 ${!letter ? 'bg-[#0A2540] text-white' : 'bg-slate-100 text-slate-600'}`}>All</button>
        {LETTERS.map((L) => (
          <button key={L} type="button" disabled={!present.has(L)} onClick={() => setLetter(letter === L ? '' : L)}
            className={`w-7 h-7 rounded-lg text-xs font-bold shrink-0 disabled:opacity-25 ${letter === L ? 'bg-[#0A2540] text-white' : 'bg-slate-100 text-slate-600'}`}>{L}</button>
        ))}
      </div>
      <ul className="border border-slate-200 rounded-xl max-h-52 overflow-y-auto divide-y divide-slate-100">
        {loading ? <li className="px-3 py-3 text-sm text-slate-500">Loading banks…</li> : !list.length ? <li className="px-3 py-3 text-sm text-slate-500">No bank matches. Clear the search or letter.</li>
          : list.map((b) => <li key={b.code}><button type="button" onClick={() => onChange(b.code)} className="w-full text-left px-3 py-2.5 text-sm hover:bg-slate-50">{b.name}</button></li>)}
      </ul>
      <p className="text-[11px] text-slate-400">{list.length} of {banks.length} banks</p>
    </div>
  );
}

const errText = (e: any) => e?.response?.data?.message ?? (e?.response?.data?.errors ? Object.values(e.response.data.errors).flat().join(' ') : 'Something went wrong.');

/** Set a first PIN, or change it (needs the current one). */
export function PinManager({ hasPin, onClose, onDone }: { hasPin: boolean; onClose: () => void; onDone: (msg: string) => void }) {
  const [cur, setCur] = useState('');
  const [pin, setPin] = useState('');
  const [conf, setConf] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const d = (v: string) => v.replace(/\D/g, '').slice(0, 4);
  const ok = pin.length === 4 && conf.length === 4 && (!hasPin || cur.length === 4);
  const save = async () => {
    if (pin !== conf) return setErr('The two new PINs are not the same.');
    if (['0000', '1111', '1234', '4321'].includes(pin)) return setErr('Choose a PIN that is harder to guess.');
    setBusy(true); setErr('');
    try {
      if (hasPin) await postAuthPinChange({ current_pin: cur, new_pin: pin, new_pin_confirmation: conf });
      else await postAuthPinSet({ pin, pin_confirmation: conf });
      onDone(hasPin ? 'Your PIN has been changed.' : 'Your PIN is set. You can now pay and send money.');
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  const box = (label: string, v: string, set: (s: string) => void, auto = false) => (
    <label className="block"><span className="text-sm font-semibold text-slate-700">{label}</span>
      <input type="password" inputMode="numeric" maxLength={4} autoFocus={auto} value={v} onChange={(e) => set(d(e.target.value))}
        className="mt-1 w-full text-center text-2xl tracking-[0.5em] font-mono border-2 border-slate-300 rounded-xl py-3 focus:border-[#0A2540] outline-none" /></label>
  );
  return (
    <Sheet title={hasPin ? 'Change transaction PIN' : 'Set your transaction PIN'} sub="4 digits. You enter it for every payment and transfer." onClose={onClose}
      footer={<><button onClick={onClose} className={ghost}>Cancel</button><button onClick={save} disabled={!ok || busy} className={primary}>{busy ? 'Saving…' : hasPin ? 'Change PIN' : 'Set PIN'}</button></>}>
      <div className="space-y-3">
        {hasPin && box('Current PIN', cur, setCur, true)}
        {box('New PIN', pin, setPin, !hasPin)}
        {box('Confirm new PIN', conf, setConf)}
        <p className="text-xs text-slate-500">Don't use 1234, 0000 or your birth year. Never share your PIN — the school and OJP will never ask for it.</p>
        {err && <p className="text-sm text-red-600">{err}</p>}
      </div>
    </Sheet>
  );
}
